import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';
import {
  Users,
  Swords,
  Trophy,
  Activity,
  RefreshCw,
  Building2,
  Gamepad2,
  TrendingUp,
  Eye,
} from 'lucide-react';
import type { DashboardStats } from 'types/admin';
import { BattleVolumeOverviewPanels, EMPTY_VOLUME_OVERVIEW } from 'components/BattleVolumeOverviewPanels';

const EMPTY_STATS: DashboardStats = {
  summary: {
    totalPlayers: 0,
    playersInGame: 0,
    runningBattles: 0,
    completedBattles: 0,
    waitingBattles: 0,
    totalPlayerBalance: 0,
    transactionsToday: 0,
    volumeToday: 0,
    totalGameVolume: 0,
    houseRakePercent: 0,
  },
  battlesByVariant: [],
  consumers: [],
  directPlatform: {
    playerCount: 0,
    totalBalance: 0,
    totalVolume: 0,
    volumeToday: 0,
    battlesPlayed: 0,
    battlesToday: 0,
  },
  consumerOverview: { ...EMPTY_VOLUME_OVERVIEW },
  normalOverview: { ...EMPTY_VOLUME_OVERVIEW },
  privateOverview: { ...EMPTY_VOLUME_OVERVIEW },
  liveSockets: {
    totalSocketConnections: 0,
    uniqueConnectedPlayers: 0,
    activeTableRooms: 0,
    connectedPlayersInTables: 0,
    source: 'unavailable',
  },
};

const money = (value: number, symbol = '') =>
  `${symbol}${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type StatCardProps = {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  iconWrap: string;
};

function StatCard({ icon: Icon, label, value, sub, iconWrap }: StatCardProps) {
  return (
    <div className="rounded-xl border border-line bg-subtle/35 p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl mb-2.5 ${iconWrap}`}>
        <Icon size={18} strokeWidth={2} />
      </div>
      <p className="text-[10px] font-bold text-muted uppercase tracking-wider leading-snug">{label}</p>
      <p className="text-xl sm:text-2xl font-extrabold text-heading mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-muted mt-1.5 leading-snug">{sub}</p>}
    </div>
  );
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardStats = () => {
    setRefreshing(true);
    api.get<DashboardStats>(API_ENDPOINTS.ADMIN.DASHBOARD.GET_STATS)
      .then(res => {
        if (res.success && res.data) {
          setStats({
            summary: { ...EMPTY_STATS.summary, ...res.data.summary },
            battlesByVariant: Array.isArray(res.data.battlesByVariant) ? res.data.battlesByVariant : [],
            consumers: Array.isArray(res.data.consumers)
              ? res.data.consumers.map((c: any) => ({
                  id: c.id,
                  consumerId: c.consumerId,
                  consumerName: c.consumerName,
                  status: c.status,
                  defaultCurrencyCode: c.defaultCurrencyCode,
                  currencySymbol: c.currencySymbol,
                  playerCount: c.playerCount ?? 0,
                  totalBalance: c.totalBalance ?? 0,
                  totalVolume: c.totalVolume ?? 0,
                  volumeToday: c.volumeToday ?? 0,
                  battlesPlayed: c.battlesPlayed ?? 0,
                  battlesToday: c.battlesToday ?? 0,
                }))
              : Array.isArray(res.data.providers)
                ? res.data.providers.map((p: any) => ({
                    consumerId: p.consumerId ?? p.providerId,
                    consumerName: p.consumerName ?? p.providerName,
                    status: p.status,
                    playerCount: p.playerCount ?? 0,
                    totalBalance: p.totalBalance ?? 0,
                    totalVolume: p.totalVolume ?? 0,
                    volumeToday: p.volumeToday ?? 0,
                    battlesPlayed: p.battlesPlayed ?? 0,
                    battlesToday: p.battlesToday ?? 0,
                  }))
                : [],
            directPlatform: {
              ...EMPTY_STATS.directPlatform,
              ...(res.data.directPlatform ?? {}),
            },
            normalOverview: {
              ...EMPTY_VOLUME_OVERVIEW,
              ...(res.data.normalOverview ?? res.data.consumerOverview ?? {}),
            },
            privateOverview: {
              ...EMPTY_VOLUME_OVERVIEW,
              ...(res.data.privateOverview ?? {}),
            },
            liveSockets: {
              ...EMPTY_STATS.liveSockets!,
              ...(res.data.liveSockets ?? {}),
            },
          });
        }
        setLoading(false);
        setRefreshing(false);
      })
      .catch(err => {
        console.error('Failed to fetch dashboard stats:', err);
        setLoading(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted font-medium">
        <RefreshCw className="animate-spin mr-2" size={20} /> Loading dashboard...
      </div>
    );
  }

  const { summary, battlesByVariant, consumers, directPlatform, normalOverview, privateOverview, liveSockets } = stats;
  const socketMetrics = liveSockets ?? EMPTY_STATS.liveSockets!;

  const normalStats = normalOverview ?? EMPTY_VOLUME_OVERVIEW;
  const privateStats = privateOverview ?? EMPTY_VOLUME_OVERVIEW;
  const primaryCurrencySymbol = consumers.find((c) => c.currencySymbol)?.currencySymbol || '';

  const kpiCards = [
    {
      icon: Users,
      label: 'Total Players',
      value: summary.totalPlayers.toLocaleString(),
      sub: `${summary.playersInGame.toLocaleString()} seated in DB · ${socketMetrics.connectedPlayersInTables.toLocaleString()} live at tables`,
      wrap: 'bg-blue-500/10 text-blue-600',
    },
    {
      icon: Activity,
      label: 'Live WebSocket Connections',
      value: socketMetrics.totalSocketConnections.toLocaleString(),
      sub:
        socketMetrics.source === 'live'
          ? `${socketMetrics.uniqueConnectedPlayers.toLocaleString()} unique players · ${socketMetrics.activeTableRooms.toLocaleString()} active rooms`
          : 'Game backend unreachable — showing 0',
      wrap: 'bg-cyan-500/10 text-cyan-600',
    },
    {
      icon: Swords,
      label: 'Running Battles',
      value: summary.runningBattles.toLocaleString(),
      sub: `${summary.waitingBattles.toLocaleString()} running in lobby`,
      wrap: 'bg-amber-500/10 text-amber-600',
    },
    {
      icon: Trophy,
      label: 'Completed Battles',
      value: summary.completedBattles.toLocaleString(),
      sub: 'All-time finished battles',
      wrap: 'bg-emerald-500/10 text-emerald-600',
    },
    {
      icon: Activity,
      label: 'Today\'s Game Volume',
      value: money(summary.volumeToday),
      sub: `${summary.transactionsToday.toLocaleString()} in-game transactions today`,
      wrap: 'bg-indigo-500/10 text-indigo-600',
    },
    {
      icon: TrendingUp,
      label: 'Total Game Volume',
      value: money(summary.totalGameVolume),
      sub: 'All-time peak battle pot across the platform',
      wrap: 'bg-violet-500/10 text-violet-600',
    },
  ];

  const totalRunningByVariant = battlesByVariant.reduce((sum, v) => sum + v.runningBattles, 0);
  const totalCompletedByVariant = battlesByVariant.reduce((sum, v) => sum + v.completedBattles, 0);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-heading">Operations Overview</h2>
          <p className="text-muted text-sm mt-1">
            Platform operations, all-consumer totals, and per-consumer breakdown.
            {summary.houseRakePercent > 0 && (
              <span className="ml-1">House rake: {summary.houseRakePercent}%.</span>
            )}
          </p>
        </div>
        <button
          onClick={fetchDashboardStats}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-card text-body border border-line rounded-xl shadow-sm hover:bg-subtle transition-all font-semibold text-sm self-start"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {kpiCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="bg-card p-5 rounded-2xl border border-line shadow-sm hover:shadow-md transition-shadow">
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl mb-3 ${card.wrap}`}>
                <Icon size={20} />
              </div>
              <p className="text-[11px] font-bold text-muted uppercase tracking-wider">{card.label}</p>
              <p className="text-2xl font-extrabold text-heading mt-1">{card.value}</p>
              <p className="text-xs text-muted mt-2 leading-snug">{card.sub}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
          <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between gap-3`}>
            <div className="flex items-center gap-2">
              <Gamepad2 size={18} className="text-brand-gold" />
              <h3 className="font-bold text-heading">Battles by Variant</h3>
            </div>
            <div className="flex items-center gap-3 text-xs font-semibold text-muted">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Running {totalRunningByVariant}</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Completed {totalCompletedByVariant}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className={LIST_TABLE_CLASS}>
              <thead className={LIST_THEAD_CLASS}>
                <tr>
                  <th className={LIST_TH_NO_CLASS}>No.</th>
                  {['Variant', 'Code', 'Running', 'Completed', 'Abandoned', 'Total'].map((h) => (
                    <th key={h} className={LIST_TH_CLASS}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={LIST_TBODY_CLASS}>
                {battlesByVariant.length > 0 ? (
                  battlesByVariant.map((variant, index) => {
                    const total = variant.runningBattles + variant.completedBattles + variant.abandonedBattles;
                    return (
                      <tr key={variant.variantId} className="hover:bg-subtle/50 transition-colors text-sm">
                        <td className={LIST_TD_NO_CLASS}>{listRowNumber(1, index, 0)}</td>
                        <td className={`${LIST_TD_CLASS} font-semibold text-heading`}>{variant.variantName}</td>
                        <td className={`${LIST_TD_CLASS} font-mono text-xs text-muted`}>{variant.variantCode}</td>
                        <td className={`${LIST_TD_CLASS}`}>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/10 text-amber-700">
                            {variant.runningBattles}
                          </span>
                        </td>
                        <td className={`${LIST_TD_CLASS}`}>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-700">
                            {variant.completedBattles}
                          </span>
                        </td>
                        <td className={`${LIST_TD_CLASS} text-muted`}>{variant.abandonedBattles}</td>
                        <td className={`${LIST_TD_CLASS} font-bold text-heading`}>{total}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-muted">No variant battle data yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
      </div>

      {/* Aggregated metrics across all registered consumers */}
      <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
        <div className="px-5 sm:px-6 py-5 border-b border-line bg-gradient-to-r from-indigo-500/5 via-card to-brand-gold/5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-600">
                <Building2 size={22} strokeWidth={2.25} />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-heading tracking-tight">All Consumer Overview</h3>
                <p className="text-sm text-muted mt-0.5">
                  Combined totals plus public lobby and private-table breakdowns across all registered consumers
                </p>
                {directPlatform.totalVolume > 0 && (
                  <p className="text-xs text-muted/90 mt-1.5">
                    Excludes Direct Platform ({money(directPlatform.totalVolume)}, {directPlatform.battlesPlayed.toLocaleString()} battles).
                    Top Total Game Volume includes the full platform.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-5">
          <BattleVolumeOverviewPanels
            normalOverview={normalStats}
            privateOverview={privateStats}
            currencySymbol={primaryCurrencySymbol}
          />
        </div>
      </div>

      {/* Per-consumer breakdown */}
      <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-brand-gold" />
            <div>
              <h3 className="font-bold text-heading">Consumers</h3>
              <p className="text-xs text-muted mt-0.5">Per-consumer breakdown · click the eye icon for full detail</p>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className={LIST_TABLE_CLASS}>
            <thead className={LIST_THEAD_CLASS}>
              <tr>
                <th className={LIST_TH_NO_CLASS}>No.</th>
                {[
                  'Consumer ID',
                  'Consumer Name',
                  'Currency',
                  'Status',
                  'Players',
                  'Total Public Volume',
                  'Today Public Volume',
                  'Public Battles',
                  'Public Today',
                  '',
                ].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-bold text-muted uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className={LIST_TBODY_CLASS}>
              {consumers.length > 0 ? (
                consumers.map((consumer, index) => (
                  <tr key={consumer.consumerId} className="hover:bg-subtle/50 transition-colors text-sm">
                    <td className={LIST_TD_NO_CLASS}>{listRowNumber(1, index, 0)}</td>
                    <td className="px-4 py-3.5 font-mono text-muted whitespace-nowrap">
                      {consumer.id ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/consumers/${consumer.id}`)}
                          className="hover:text-brand-gold hover:underline transition-colors"
                        >
                          {consumer.consumerId}
                        </button>
                      ) : (
                        consumer.consumerId
                      )}
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-heading whitespace-nowrap">
                      {consumer.id ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/consumers/${consumer.id}`)}
                          className="hover:text-brand-gold hover:underline transition-colors text-left"
                        >
                          {consumer.consumerName}
                        </button>
                      ) : (
                        consumer.consumerName
                      )}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-sm font-semibold text-heading">
                      {consumer.currencySymbol ? `${consumer.currencySymbol} ${consumer.defaultCurrencyCode || ''}` : (consumer.defaultCurrencyCode || '—')}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                        consumer.status ? 'bg-emerald-500/10 text-emerald-700' : 'bg-red-500/10 text-brand-danger'
                      }`}>
                        {consumer.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-heading tabular-nums">{consumer.playerCount.toLocaleString()}</td>
                    <td className="px-4 py-3.5 font-semibold text-heading tabular-nums whitespace-nowrap">
                      {money(consumer.totalVolume, consumer.currencySymbol || '')}
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-indigo-700 tabular-nums whitespace-nowrap">
                      {money(consumer.volumeToday, consumer.currencySymbol || '')}
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-heading tabular-nums">{consumer.battlesPlayed.toLocaleString()}</td>
                    <td className="px-4 py-3.5 font-semibold text-heading tabular-nums">{consumer.battlesToday.toLocaleString()}</td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      {consumer.id ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/consumers/${consumer.id}`)}
                          className="inline-flex items-center justify-center p-2 rounded-lg border border-blue-200 bg-blue-500/10 text-blue-600 hover:bg-blue-500/15 hover:text-blue-700 transition-all"
                          title="View consumer details"
                        >
                          <Eye size={16} />
                        </button>
                      ) : (
                        <span className="text-muted text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={11} className="px-6 py-10 text-center text-muted">No consumers configured.</td>
                </tr>
              )}
              {(directPlatform.playerCount > 0 || directPlatform.battlesPlayed > 0) && (
                <tr className="bg-subtle/40 text-sm">
                  <td className="px-4 py-3.5 font-mono text-muted">—</td>
                  <td className="px-4 py-3.5 font-semibold text-heading">Direct Platform</td>
                  <td className="px-4 py-3.5 text-muted">—</td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-500/10 text-blue-700">Internal</span>
                  </td>
                  <td className="px-4 py-3.5 font-semibold text-heading tabular-nums">{directPlatform.playerCount.toLocaleString()}</td>
                  <td className="px-4 py-3.5 font-semibold text-heading tabular-nums">{money(directPlatform.totalVolume)}</td>
                  <td className="px-4 py-3.5 font-semibold text-indigo-700 tabular-nums">{money(directPlatform.volumeToday)}</td>
                  <td className="px-4 py-3.5 font-semibold text-heading tabular-nums">{directPlatform.battlesPlayed.toLocaleString()}</td>
                  <td className="px-4 py-3.5 font-semibold text-heading tabular-nums">{directPlatform.battlesToday.toLocaleString()}</td>
                  <td className="px-4 py-3.5 text-muted text-xs">—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
